import { ClipSource } from "../NOKit";
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { useAPI } from "../../../contexts/APIContext";
import { showToast } from "../../Toast";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const InterestsStep = () => {
  const { selectedInterests, setSelectedInterests, nextStep, username } = useOnboarding();
  const { apiClient } = useAPI();
  const [customInterest, setCustomInterest] = useState("");
  const [portalTarget, setPortalTarget] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const interestEmojis = {
    Animals: "🐾", Space: "🚀", Dinosaurs: "🦕", Art: "🎨",
    Music: "🎵", Sports: "⚽️", Science: "🔬", Magic: "✨",
    Adventure: "🗺️", Fantasy: "🧚", Robots: "🤖", Nature: "🌿",
    Ocean: "🌊", Cooking: "👨‍🍳", Dancing: "💃", Reading: "📚",
    Building: "🏗️", Cars: "🚗", Superheroes: "🦸", Princess: "👸",
    Pirates: "🏴‍☠️", Knights: "⚔️", Dragons: "🐉", Unicorns: "🦄",
    Gaming: "🎮", Movies: "🎬", Travel: "✈️", Food: "🍕",
    Pets: "🐶", Fashion: "👗",
  };

  const getEmoji = (interest) => {
    if (interestEmojis[interest]) return interestEmojis[interest];
    const match = Object.keys(interestEmojis).find((key) => key.toLowerCase() === interest.toLowerCase());
    return match ? interestEmojis[match] : "⭐";
  };

  const MAX_INTERESTS = 3;
  const toggleInterest = (interest) => {
    setSelectedInterests((prev) => {
      if (prev.includes(interest)) return prev.filter((i) => i !== interest);
      if (prev.length >= MAX_INTERESTS) return prev; // iOS caps picks at 3
      return [...prev, interest];
    });
  };

  const addCustomInterest = async () => {
    const trimmed = customInterest.trim();
    if (!trimmed || selectedInterests.includes(trimmed)) return;

    if (apiClient) {
      setIsChecking(true);
      try {
        const result = await apiClient.checkTextSafety(trimmed);
        if (result?.safe === false) {
          showToast("That interest isn't appropriate for Pixley. Try something else!", "error");
          setCustomInterest("");
          setIsChecking(false);
          return;
        }
      } catch (err) { /* allow through on API failure */ }
      setIsChecking(false);
    }

    setSelectedInterests((prev) => [...prev, trimmed]);
    setCustomInterest("");
  };

  const allInterests = [
    ...Object.keys(interestEmojis),
    ...selectedInterests.filter((i) => !interestEmojis[i]),
  ];

  const filteredInterests = allInterests
    .filter((i) => !customInterest.trim() || i.toLowerCase().includes(customInterest.toLowerCase()))
    .sort((a, b) => {
      const aS = selectedInterests.includes(a);
      const bS = selectedInterests.includes(b);
      if (aS && !bS) return -1;
      if (!aS && bS) return 1;
      return 0;
    });

  const canContinue = selectedInterests.length > 0;

  const continueButton = (
    <button
      onClick={nextStep}
      disabled={!canContinue}
      className="inline-flex items-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: canContinue ? "pointer" : "not-allowed",
      }}
    >
      Continue
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  const isSearching = customInterest.trim().length > 0;
  const visibleInterests = isSearching ? filteredInterests : showAll ? filteredInterests : filteredInterests.slice(0, 12);

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      {/* Panda + Speech Bubble */}
      <div className="flex items-start gap-4 sm:gap-5 mb-6 w-full">
        <div className="flex-shrink-0">
          <div className="relative w-24 h-24 sm:w-32 sm:h-32">
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              poster="/assets/pixley-panda-nobg.png"
              className="relative w-full h-full object-contain z-10 pointer-events-none"
              style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
            >
              <ClipSource clip="panda_wave" />
            </video>
          </div>
        </div>
        <div className="flex-1 relative">
          <div
            className="absolute z-0"
            style={{
              left: -8, top: 28, width: 16, height: 16,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRight: "none", borderTop: "none",
              transform: "rotate(45deg)",
            }}
          />
          <div
            className="relative z-10 px-6 py-5 sm:px-7 sm:py-6"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 22,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
            }}
          >
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              Interests
            </span>
            <h1
              style={{
                ...FREDOKA,
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(1.3rem, 2.4vw, 1.7rem)",
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                margin: "6px 0 6px",
              }}
            >
              What does <span style={{ color: "#0065F8" }}>{username || "your child"}</span> love?
            </h1>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              Pick up to 3 favorites — their stories will be built around them.
            </p>
          </div>
        </div>
      </div>

      {/* Search / Add custom */}
      <div className="w-full mb-4">
        <div className="relative">
          <div
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "rgba(0,101,248,0.10)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <svg width="14" height="14" fill="none" stroke="#0065F8" strokeWidth="2.8" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
          </div>
          <input
            type="text"
            value={customInterest}
            onChange={(e) => setCustomInterest(e.target.value)}
            onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomInterest(); } }}
            placeholder="Search or add a custom interest"
            className="w-full transition-all"
            style={{
              ...FREDOKA,
              background: "#FFFFFF",
              color: "#0B1A33",
              fontSize: 14,
              fontWeight: 500,
              border: "2px solid rgba(11,26,51,0.08)",
              borderRadius: 999,
              padding: "12px 110px 12px 50px",
              outline: "none",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
          />
          {customInterest.trim() && (
            <button
              onClick={addCustomInterest}
              disabled={isChecking}
              className="transition active:scale-[0.97] disabled:opacity-50"
              style={{
                ...FREDOKA,
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 12,
                border: "none",
                borderRadius: 999,
                padding: "7px 14px",
                boxShadow: "0px 2px 0px #073B8E",
                cursor: "pointer",
              }}
            >
              {isChecking ? "…" : "+ Add"}
            </button>
          )}
        </div>
        <p className="mt-2 ml-2" style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, fontWeight: 600 }}>
          <strong style={{ color: "#0065F8" }}>{selectedInterests.length}</strong> interest{selectedInterests.length !== 1 ? "s" : ""} selected
        </p>
      </div>

      {/* Interest tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full">
        {visibleInterests.map((interest) => {
          const isSelected = selectedInterests.includes(interest);
          const emoji = getEmoji(interest);
          return (
            <div
              key={interest}
              onClick={() => toggleInterest(interest)}
              className="relative cursor-pointer overflow-hidden transition-all active:scale-[0.97]"
              style={{
                height: 116,
                background: isSelected ? "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" : "#FFFFFF",
                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                borderRadius: 18,
                boxShadow: isSelected
                  ? "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)"
                  : "0px 3px 0px rgba(11,26,51,0.06)",
              }}
            >
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  fontSize: 64,
                  opacity: isSelected ? 0.30 : 0.18,
                  filter: isSelected ? "none" : "grayscale(30%)",
                  transition: "all 200ms",
                }}
              >
                {emoji}
              </div>
              <div
                className="absolute"
                style={{
                  bottom: 12,
                  left: 14,
                  ...FREDOKA,
                  color: isSelected ? "#FFFFFF" : "#0B1A33",
                  fontWeight: 700,
                  fontSize: 15,
                  textTransform: "uppercase",
                  letterSpacing: "-0.01em",
                  textShadow: isSelected ? "0px 1px 2px rgba(7,59,142,0.40)" : "none",
                }}
              >
                {interest}
              </div>
              {isSelected ? (
                <div
                  className="absolute"
                  style={{
                    top: 8, right: 8,
                    width: 22, height: 22,
                    background: "#FFFFFF",
                    color: "#0065F8",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 2px 4px rgba(7,59,142,0.30)",
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {!isSearching && filteredInterests.length > 12 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 inline-flex items-center gap-1.5 transition active:scale-[0.97]"
          style={{
            ...FREDOKA,
            background: "transparent",
            color: "#0065F8",
            fontWeight: 700,
            fontSize: 13,
            border: "none",
            cursor: "pointer",
          }}
        >
          <span>{showAll ? "Show less" : `See all ${filteredInterests.length} interests`}</span>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24" style={{ transform: showAll ? "rotate(180deg)" : "none" }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {portalTarget && ReactDOM.createPortal(continueButton, portalTarget)}
    </div>
  );
};

export default InterestsStep;
